import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner, UncontrolledTooltip } from 'reactstrap'
import { IconX } from '@tabler/icons-react'
import { useFetch } from '../../../_hooks/useFetch'
import _endpoints from '../../../_endpoints'
import { setProducts } from '../../../_redux/actions/dataMarketPlaceActions'
import { useDataboxService } from '../Teams/services/databoxService'
// @ts-ignore
import { Badge } from 'dsm-react-lib'
import DataSourcesModal, { DataSourceTab } from './DataSourcesModal'
import RemoveAllDataSourcesModal from './modals/RemoveAllDataSourcesModal'
import { buildAgentConfigSnapshot, isSelectionEqualToAgentConfig } from './utils/mcpServerUtils'
import { isLockedItem } from '../Teams/modals/lockUtils'

type PendingDataSourceSnapshot = {
  selectedDataboxes: Array<{
    id: string
    name: string
    smartDocsDataboxId?: string
    teamIdentifier?: string
    selectedItems?: any[]
  }>
  selectedDataProducts: Array<{ id: string; name: string }>
}

// Module-level cache of the live selection for a chat with no confirmed thread-level source yet - survives the remount right after the 1st message.
let pendingNewChatDataSources: PendingDataSourceSnapshot | null = null
// Agent this cache belongs to - lets a fresh instance detect/discard a stale cache post-remount.
let pendingNewChatDataSourcesAgentId: string | undefined = undefined

// Explicitly discards the stale in-progress-chat cache - needed when chatId stays null -> null (e.g. databox "Chat" button navigation).
export const resetPendingDataSourcesCache = () => {
  pendingNewChatDataSources = null
  pendingNewChatDataSourcesAgentId = undefined
}

const filterLockedSelectedItems = (items: any[] = [], lockedItemIds: Set<string>) =>
  items.filter((item: any) => isLockedItem(item, lockedItemIds))

interface DataSourceSelectProps {
  dataProductsInChat?: any
  isMarketplace?: boolean
  isDisabled?: boolean
  // Grouped source configuration
  agentSources?: {
    databoxIds: string[]
    dataProductIds: string[]
    agentId?: string // For change detection
    teamIdentifiers?: Record<string, string> // Map of databoxId -> teamIdentifier
    cognitiveSearchIds?: string[] // File-level scope (cognitive search IDs) from the agent's filter
    dataProductsByDatabox?: Array<{ dataProductId: string; databoxId: string }> // Data products nested inside a databox
    smartInsightsTeamIdentifier?: string // Raw teamIdentifier from the agent's SmartInsights scope config
    // Team-agent context, forwarded to the Databoxes tab to lock team selection (no switching/breadcrumb).
    isTeamAgent?: boolean
    // Enterprise-agent context, forwarded to the Databoxes tab to restrict it to the Community team.
    isEnterpriseAgent?: boolean
    agentScopeId?: string
    // The agent's own default Personal Databox selection (file ids) - falls back into use only
    // until the thread has its own confirmed personalDataboxResourceIds.
    personalDataboxResourceIds?: string[]
    personalDataBoxId?: string
  }
  threadSources?: {
    databoxIds: string[]
    dataProductIds: string[]
    // File-level scope narrowed for THIS thread - takes priority over agentSources.cognitiveSearchIds
    cognitiveSearchIds?: string[]
    // Data products narrowed/nested inside a databox for THIS thread (same idea as above)
    dataProductsByDatabox?: Array<{ dataProductId: string; databoxId: string }>
    // This thread's Personal Databox selection (file ids)
    personalDataboxResourceIds?: string[]
    // This thread's Personal Databox id (from the separate top-level personalDataBoxes field)
    personalDataBoxId?: string
  }
  // Simplified chat state
  isExistingChat?: boolean
  hasMessages?: boolean
  // Current chat/thread id (null for a not-yet-persisted chat) - used to detect switching
  // between two different chats, which isExistingChat alone can't distinguish
  chatId?: string | null
  // True while an existing chat's thread data sources are still being fetched, so init doesn't
  // lock in the agent's defaults before the thread's own sources have loaded
  isFetchingThreadData?: boolean
  // Callback when data sources change - provides only the data needed for submission
  onDataSourcesChange?: (data: {
    selectedDataboxes: any[]
    selectedDataProducts: any[]
    combinedDataboxIds: string[]
    combinedDataProductIds: string[]
  }) => void
  isResolvingDataSources?: boolean
  setIsResolvingDataSources: React.Dispatch<React.SetStateAction<boolean>>
  // Methods to open modals (exposed via ref)
  ref?: React.Ref<{
    openSourcesModal: () => void
  }>
}

export const DataSourceSelect = ({
  dataProductsInChat,
  isMarketplace = false,
  isDisabled = false,
  agentSources = {
    databoxIds: [],
    dataProductIds: [],
    agentId: undefined,
    teamIdentifiers: {},
    cognitiveSearchIds: [],
    dataProductsByDatabox: [],
    smartInsightsTeamIdentifier: undefined,
    isTeamAgent: false,
    isEnterpriseAgent: false,
    agentScopeId: undefined,
    personalDataboxResourceIds: [],
    personalDataBoxId: undefined
  },
  threadSources = {
    databoxIds: [],
    dataProductIds: [],
    cognitiveSearchIds: [],
    dataProductsByDatabox: [],
    personalDataboxResourceIds: []
  },
  isExistingChat = false,
  hasMessages = false,
  chatId = null,
  isFetchingThreadData = false,
  onDataSourcesChange,
  isResolvingDataSources,
  setIsResolvingDataSources,
  ref
}: DataSourceSelectProps) => {
  const [isFetchingDatasets, getDatasets] = useFetch('semanticdata')
  const [databoxes, setDataboxes] = useState<any[]>([])
  const [agentDataProducts, setAgentDataProducts] = useState<any[]>([])
  // Files scoped within a databox (databoxId -> file list), so specific in-scope files can
  // be shown/unchecked instead of always treating the databox as fully selected
  const [filesByDatabox, setFilesByDatabox] = useState<Record<string, any[]>>({})
  // Data products nested inside a databox (databoxId -> product list), shown/unchecked the same way
  const [productsByDatabox, setProductsByDatabox] = useState<Record<string, any[]>>({})
  // True while the two effects below resolve file/product-level scope - the badge hides its
  // count until both finish so the number doesn't change after it's first shown
  const [isFetchingFileDetails, setIsFetchingFileDetails] = useState(false)
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState(false)
  const [isFetchingSources, setIsFetchingSources] = useState(false)
  // True while the thread's Personal Databox resource ids are being resolved into full file
  const [isFetchingPersonalDatabox, setIsFetchingPersonalDatabox] = useState(false)
  const products = useSelector((state: any) => state?.dataMarketPlaceReducer?.products)
  const dispatch = useDispatch()
  const {
    getDataboxInfo,
    getDataProduct,
    getDocumentDetails,
    getMyDataboxes,
    getDataboxDocumentsPaginated
  } = useDataboxService()
  const fetchedIdsRef = useRef<Set<string>>(new Set())
  const isInitializedRef = useRef(false)
  const previousAgentIdRef = useRef(agentSources.agentId)
  const previousChatIdRef = useRef(chatId)
  const hasUserInteractedRef = useRef(false) // Track if user has manually changed selection
  // Last threadSources key used - lets the init effect self-correct after a stale/empty run
  const previousThreadSourcesKeyRef = useRef<string>('')
  // Bumped whenever the init effect re-seeds selectedDataboxes (dropping selectedItems), so the
  // attach effect re-applies file/product narrowing even when the fetched maps didn't change
  // (e.g. same agent -> switch chat -> New chat yields the same scope keys, so no refetch).
  const [selectionInitVersion, setSelectionInitVersion] = useState(0)

  // Internal state for data source selection, Store as objects
  const [selectedDataboxes, setSelectedDataboxes] = useState<
    Array<{
      id: string
      name: string
      smartDocsDataboxId?: string
      teamIdentifier?: string
      selectedItems?: any[]
    }>
  >([])
  const [selectedDataProducts, setSelectedDataProducts] = useState<
    Array<{ id: string; name: string }>
  >([])

  // Single shared modal state (Databoxes/Data Products)
  const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(false)
  const [activeSourceTab, setActiveSourceTab] = useState<DataSourceTab>('personalDatabox')
  // Hover-to-X + confirm modal for clearing every source selected on this thread at once
  const [isDataSourcesBadgeHovered, setIsDataSourcesBadgeHovered] = useState(false)
  const [isRemoveAllSourcesModalOpen, setIsRemoveAllSourcesModalOpen] = useState(false)

  // Initialize selected objects when agent changes OR when loading existing chat
  useEffect(() => {
    // Detect if agent changed
    const agentChanged = previousAgentIdRef.current !== agentSources.agentId
    // Detect the chat changing - covers "New chat" and switching between existing chats.
    // Excludes null -> non-null (a new chat's first real thread id) to preserve in-progress selections.
    const chatChanged =
      previousChatIdRef.current !== chatId &&
      !(previousChatIdRef.current === null && chatId !== null)
    previousChatIdRef.current = chatId

    if (agentChanged || chatChanged) {
      previousAgentIdRef.current = agentSources.agentId
      isInitializedRef.current = false
      fetchedIdsRef.current.clear()
      hasUserInteractedRef.current = false // Reset interaction flag for new agent/chat
      pendingNewChatDataSources = null
      pendingNewChatDataSourcesAgentId = undefined
    }

    // `agentChanged` above is always false on a fresh instance's first run (the ref seeds from
    // the current render) - verify against the module-level owner tag instead, which survives.
    if (pendingNewChatDataSources && pendingNewChatDataSourcesAgentId !== agentSources.agentId) {
      pendingNewChatDataSources = null
      pendingNewChatDataSourcesAgentId = undefined
    }

    // Wait for an existing chat's thread data to finish loading before locking in a selection.
    // Must run before the "already initialized" check: on a fresh page load this effect can run
    // once before isExistingChat/isFetchingThreadData flip true, so this ensures that later run isn't skipped.
    if (isExistingChat && isFetchingThreadData) {
      isInitializedRef.current = false
      return
    }

    // Skip reset if already initialized and agent/chat hasn't changed - UNLESS threadSources
    // itself changed since we last used it and the user hasn't manually edited the selection
    // (self-heals a premature/empty init without clobbering the user's own in-progress edits).
    const threadSourcesKey = `${threadSources.databoxIds.join(
      ','
    )}|${threadSources.dataProductIds.join(',')}`
    const threadSourcesChanged =
      previousThreadSourcesKeyRef.current !== threadSourcesKey && !hasUserInteractedRef.current
    if (isInitializedRef.current && !agentChanged && !chatChanged && !threadSourcesChanged) {
      return
    }
    previousThreadSourcesKeyRef.current = threadSourcesKey

    let databoxIds: string[]
    let dataProductIds: string[]

    // Priority: (1) cached edit, (2) thread's confirmed sources, (3) agent defaults - gated on
    // an actual sent/existing conversation so a brand-new composer ignores a stale cache.
    const hasExistingConversationSignal =
      hasMessages ||
      threadSources.databoxIds.length > 0 ||
      threadSources.dataProductIds.length > 0 ||
      (threadSources.personalDataboxResourceIds?.length ?? 0) > 0

    if (hasExistingConversationSignal && pendingNewChatDataSources) {
      // Prefer the cached live selection over threadSources even if the latter's top-level ids
      // loaded - nested file/product narrowing can still be mid-flight or unnarrowed.
      setSelectedDataboxes(pendingNewChatDataSources.selectedDataboxes)
      setSelectedDataProducts(pendingNewChatDataSources.selectedDataProducts)
      setDataboxes(
        pendingNewChatDataSources.selectedDataboxes.map((db) => ({
          label: db.name || db.smartDocsDataboxId || db.id,
          value: db.smartDocsDataboxId || db.id,
          type: 'databox',
          ...(db.teamIdentifier && { teamIdentifier: db.teamIdentifier })
        }))
      )
      setAgentDataProducts(
        pendingNewChatDataSources.selectedDataProducts.map((dp) => ({
          label: dp.name || dp.id,
          value: dp.id,
          type: 'dataProduct'
        }))
      )
      isInitializedRef.current = true
      // Marks user-interacted so the "attach fetched files/products" effect below doesn't
      // clobber this restored narrowed selection with the agent's unnarrowed original scope.
      hasUserInteractedRef.current = true
      return
    } else {
      // Use thread's value per field, or agent's default if thread has none
      databoxIds =
        threadSources.databoxIds.length > 0 ? threadSources.databoxIds : agentSources.databoxIds
      dataProductIds =
        threadSources.dataProductIds.length > 0
          ? threadSources.dataProductIds
          : agentSources.dataProductIds
    }

    // Placeholders until the real data arrives and populates the resolved labels
    const databoxObjects = databoxIds.map((id) => ({
      id,
      name: '', // Placeholder - will be populated after API fetch completes
      smartDocsDataboxId: id
    }))
    const dataProductObjects = dataProductIds.map((id) => ({
      id,
      name: '' // Placeholder - will be populated after API fetch completes
    }))

    // Seed the internal name-resolution cache (databoxes/agentDataProducts state) so the
    // enrichment effects below can immediately fill in a fallback label (the raw id)
    const databoxOptions = databoxIds.map((id) => ({
      label: id,
      value: id,
      type: 'databox',
      ...(agentSources.teamIdentifiers?.[id] && {
        teamIdentifier: agentSources.teamIdentifiers[id]
      })
    }))
    const dataProductOptions = dataProductIds.map((id) => ({
      label: id,
      value: id,
      type: 'dataProduct'
    }))

    // Preserve the Personal Databox entry - it's owned by the separate resolver effect below, not this one.
    setSelectedDataboxes((prev) => [
      ...databoxObjects,
      ...prev.filter((db: any) => db.scope === 'Personal')
    ])
    setSelectedDataProducts(dataProductObjects)
    // Seed the name-resolution cache so selectedItems sync can find matches immediately
    setDataboxes(databoxOptions)
    setAgentDataProducts(dataProductOptions)
    setSelectionInitVersion((v) => v + 1)
    isInitializedRef.current = true
  }, [
    agentSources.agentId,
    agentSources.databoxIds,
    agentSources.dataProductIds,
    threadSources.databoxIds,
    threadSources.dataProductIds,
    isExistingChat,
    hasMessages,
    chatId,
    isFetchingThreadData
  ])

  // Resolve this thread's Personal Databox selection (file ids only) into a full databox entry.
  // The backend only persists the raw file ids for a thread's Personal Databox scope.
  const personalDataboxResourceIds = threadSources.personalDataboxResourceIds?.length
    ? threadSources.personalDataboxResourceIds
    : agentSources.personalDataboxResourceIds || []
  const personalDataBoxId = threadSources.personalDataBoxId ?? agentSources.personalDataBoxId
  // Joined into a stable string so the effect only re-runs when the actual ids change, not
  // whenever the parent passes a new-but-equal array reference on every render.
  const personalDataboxResourceIdsKey = personalDataboxResourceIds.join(',')
  // Everything that defines file scope, excluding chatId
  const fileScopeKey = `${personalDataboxResourceIdsKey}|${personalDataBoxId}|${agentSources.agentId}`
  const previousFileScopeKeyRef = useRef(fileScopeKey)
  const previousResolvedChatIdRef = useRef(chatId)

  useEffect(() => {
    // Skip when only chatId went null -> real; the file scope itself hasn't changed.
    const isFirstThreadIdAssignmentOnly =
      previousFileScopeKeyRef.current === fileScopeKey &&
      previousResolvedChatIdRef.current === null &&
      chatId !== null
    previousFileScopeKeyRef.current = fileScopeKey
    previousResolvedChatIdRef.current = chatId
    if (isFirstThreadIdAssignmentOnly && isInitializedRef.current) return

    // Skip once the user has manually edited the selection (or it was restored from the
    // cache after a remount) - otherwise this effect can wipe it out before threadSources loads.
    if (hasUserInteractedRef.current) return

    if (personalDataboxResourceIdsKey === '') {
      setSelectedDataboxes((prev) => prev.filter((db: any) => (db as any).scope !== 'Personal'))
      setIsFetchingPersonalDatabox(false)
      return
    }

    // Guards against setting state after this run is superseded by a newer one (ids/chat
    // changed again) or the component unmounts mid-fetch.
    let cancelled = false
    setIsFetchingPersonalDatabox(true)
    ;(async () => {
      try {
        // Step 1: Find the user's own Personal Databox (as opposed to a Team Databox)
        const myDataboxesResponse = await getMyDataboxes()
        const myDataboxes = myDataboxesResponse?.items ?? myDataboxesResponse ?? []
        const personalDatabox = (Array.isArray(myDataboxes) ? myDataboxes : []).find((db: any) =>
          personalDataBoxId
            ? String(db?.id) === String(personalDataBoxId) ||
              String(db?.smartDocsDataboxId) === String(personalDataBoxId)
            : String(db?.scope ?? '').toLowerCase() === 'personal'
        )
        if (!personalDatabox || cancelled) return

        // Step 2: Fetch that databox's documents so the bare ids can be resolved into full
        // file objects (name, size, etc).
        const docsResponse = await getDataboxDocumentsPaginated(
          personalDatabox.id,
          1,
          100,
          undefined,
          'uploadTime',
          'desc'
        )
        const docs = Array.isArray(docsResponse) ? docsResponse : []
        // Step 3: Narrow down to just the files actually selected on this thread (ignore the
        // rest of the user's personal files).
        const resourceIdSet = new Set(personalDataboxResourceIds.map(String))
        const matchedDocs = docs.filter((doc: any) =>
          resourceIdSet.has(String(doc.id ?? doc.smartDocsDocumentId ?? doc.externalId))
        )
        // If nothing matched (e.g. the files were deleted since), leave the selection as-is
        // rather than adding an empty/broken Personal entry.
        if (cancelled || matchedDocs.length === 0) return

        // Step 4: Build the databox-shaped entry, with the matched files as `selectedItems`
        // so it renders/narrows the same way a Team Databox with specific files chosen does.
        const personalDataboxItem: any = {
          id: String(personalDatabox.id),
          smartDocsDataboxId: String(personalDatabox.id),
          name: personalDatabox.name ?? 'My Personal Databox',
          scope: 'Personal',
          selectedItems: matchedDocs.map((doc: any) => ({
            id: String(doc.id ?? doc.smartDocsDocumentId ?? doc.externalId),
            name: doc.name ?? 'Untitled file',
            type: 'file',
            sizeFormatted: doc.sizeFormatted
          }))
        }

        // Step 5: Merge it in, replacing any stale Personal entry from a previous run.
        setSelectedDataboxes((prev) => [
          ...prev.filter((db: any) => (db as any).scope !== 'Personal'),
          personalDataboxItem
        ])
      } catch (err) {
        console.error('Error resolving thread Personal Databox selection:', err)
      } finally {
        if (!cancelled) setIsFetchingPersonalDatabox(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // Only ids/personalDataBoxId/chatId should trigger a re-resolve
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileScopeKey, chatId])

  // Helper to derive IDs from objects
  const getDataboxId = (db: any) => db.smartDocsDataboxId || db.id
  const getDataProductId = (dp: any) => dp.id

  // Writes only from explicit user add/remove handlers, merging onto the cache itself (not
  // React state) so two handlers firing back-to-back in one click compose correctly.
  const updatePendingDataSourcesCache = (update: Partial<PendingDataSourceSnapshot>) => {
    const base: PendingDataSourceSnapshot = pendingNewChatDataSources ?? {
      selectedDataboxes,
      selectedDataProducts
    }
    const merged = { ...base, ...update }
    pendingNewChatDataSources =
      merged.selectedDataboxes.length > 0 || merged.selectedDataProducts.length > 0 ? merged : null
    // Tag the cache with the agent it was written for (see the module-level comment above) so
    // a later, unrelated instance can tell it doesn't actually apply and ignore/discard it.
    pendingNewChatDataSourcesAgentId = pendingNewChatDataSources ? agentSources.agentId : undefined
  }

  // NOTE: not cleared when `threadSources` becomes non-empty (its nested detail can still be
  // loading) - only on a genuine agent/chat change, since restoring already sets `hasUserInteractedRef`.

  // COMBINED IDs: Currently selected sources only
  // These represent what will be sent to the backend in the chat request
  const combinedDataboxIds = React.useMemo(() => {
    return [...new Set(selectedDataboxes.map(getDataboxId))]
  }, [selectedDataboxes])

  const combinedDataProductIds = React.useMemo(() => {
    return [...new Set(selectedDataProducts.map(getDataProductId))]
  }, [selectedDataProducts])

  // Total "items" selected: a fully-selected databox counts as 1, a databox narrowed to
  // specific files/products counts as however many of those are selected, plus each standalone
  // data product. Drives the simple "Data sources (N)" badge below.
  const totalDataSourceCount = React.useMemo(() => {
    const databoxItemCount = selectedDataboxes.reduce((sum, db) => {
      const items = db.selectedItems || []
      return sum + (items.length > 0 ? items.length : 1)
    }, 0)
    return databoxItemCount + selectedDataProducts.length
  }, [selectedDataboxes, selectedDataProducts])

  // All sources from every origin (agent defaults + thread history + user selections) -
  // determines what options appear in the dropdown and what data to fetch
  const availableDataboxIds = React.useMemo(() => {
    const combined = [
      ...agentSources.databoxIds, // From agent configuration
      ...threadSources.databoxIds, // From existing chat thread
      ...selectedDataboxes.map((db: any) => db.smartDocsDataboxId || db.id) // User-added via modal
    ]
    return [...new Set(combined)].filter((id) => {
      if (!id) return false
      if (typeof id === 'string' && id.trim().length === 0) return false
      return true
    })
  }, [agentSources.databoxIds, threadSources.databoxIds, selectedDataboxes])

  const availableDataProductIds = React.useMemo(() => {
    const combined = [
      ...agentSources.dataProductIds, // From agent configuration
      ...threadSources.dataProductIds, // From existing chat thread
      ...selectedDataProducts.map((dp: any) => dp.id) // User-added via modal
    ]
    return [...new Set(combined)].filter((id) => {
      if (!id) return false
      if (typeof id === 'string' && id.trim().length === 0) return false
      return true
    })
  }, [agentSources.dataProductIds, threadSources.dataProductIds, selectedDataProducts])

  const hasAvailableDataSources = React.useMemo(() => {
    return availableDataboxIds.length > 0 || availableDataProductIds.length > 0
  }, [availableDataboxIds, availableDataProductIds])

  const agentConfigSnapshot = React.useMemo(
    () => buildAgentConfigSnapshot(agentSources),
    [
      agentSources.databoxIds,
      agentSources.personalDataBoxId,
      agentSources.dataProductIds,
      agentSources.cognitiveSearchIds,
      agentSources.personalDataboxResourceIds,
      agentSources.dataProductsByDatabox
    ]
  )
  const { lockedDataboxIds, lockedDataProductIds, lockedItemIds } = agentConfigSnapshot

  const isMatchingAgentConfig = React.useMemo(
    () =>
      isSelectionEqualToAgentConfig(selectedDataboxes, selectedDataProducts, agentConfigSnapshot),
    [selectedDataboxes, selectedDataProducts, agentConfigSnapshot]
  )

  const existingDataboxesForModal = React.useMemo(() => selectedDataboxes, [selectedDataboxes])

  const existingDataProductsForModal = React.useMemo(
    () => selectedDataProducts,
    [selectedDataProducts]
  )

  // Clears every source selected on this thread at once (agent-level defaults living outside
  // this component's state are untouched - see RemoveAllDataSourcesModal's copy).
  const removeAllDataSources = () => {
    hasUserInteractedRef.current = true
    const lockedDataboxSelection = selectedDataboxes
      .map((db) => ({
        ...db,
        selectedItems: filterLockedSelectedItems(db.selectedItems || [], lockedItemIds)
      }))
      .filter(
        (db) =>
          lockedDataboxIds.has(String(db.smartDocsDataboxId || db.id)) ||
          (db.selectedItems?.length ?? 0) > 0
      )
    const lockedDataProductsSelection = selectedDataProducts.filter((dp) =>
      lockedDataProductIds.has(String(dp.id))
    )
    setSelectedDataboxes(lockedDataboxSelection)
    setSelectedDataProducts(lockedDataProductsSelection)
    updatePendingDataSourcesCache({
      selectedDataboxes: lockedDataboxSelection,
      selectedDataProducts: lockedDataProductsSelection
    })
    setIsRemoveAllSourcesModalOpen(false)
  }

  // Allows ChatInput (parent) to programmatically open the shared sources modal
  React.useImperativeHandle(ref, () => ({
    openSourcesModal: () => {
      setActiveSourceTab('personalDatabox')
      setIsSourcesModalOpen(true)
    }
  }))

  // Notify parent when data sources change
  // Only notify after initialization completes to prevent clearing context
  useEffect(() => {
    if (onDataSourcesChange && isInitializedRef.current) {
      onDataSourcesChange({
        selectedDataboxes,
        selectedDataProducts,
        combinedDataboxIds,
        combinedDataProductIds
      })
    }
  }, [
    selectedDataboxes.map((db) => db.id || db.smartDocsDataboxId).join(','),
    // Also react to file/product-level narrowing within an already-selected databox, since
    // that doesn't change the databox id list above
    selectedDataboxes
      .map((db) => (db.selectedItems || []).map((item: any) => item.id).join('|'))
      .join(','),
    selectedDataProducts.map((dp) => dp.id).join(','),
    combinedDataboxIds.join(','),
    combinedDataProductIds.join(','),
    onDataSourcesChange
  ])

  useEffect(() => {
    // Prevents setState calls on unmounted component during async operations
    let isMounted = true

    // Only fetch datasets for marketplace mode if not already fetched. Results are merged
    // straight into agentDataProducts so marketplace products show up as options in the same
    // dropdown used everywhere else, instead of a separate marketplace-only list.
    if (isMarketplace && !hasAvailableDataSources && agentDataProducts.length === 0) {
      const getDatasetsList = async () => {
        const response = await getDatasets(`${_endpoints.dataMarketplace.getDatasets}`)

        // Transform to array of { label, value, type } using object.name - only include
        // products that are actually chat-enabled, nothing added automatically
        const dataProducts = Array.isArray(response?.dataProducts)
          ? response.dataProducts
              .filter((obj: any) => obj.isChatEnabled)
              .map((obj: any) => ({ label: obj.name, value: obj.id, type: 'dataProduct' }))
          : []

        // Only update state if component is still mounted
        if (isMounted && dataProducts.length > 0) {
          setAgentDataProducts((prev) => [
            ...prev,
            ...dataProducts.filter(
              (dp: any) => !prev.some((existing) => existing.value === dp.value)
            )
          ])
        }
      }

      getDatasetsList()
    }

    // Cleanup: Mark component as unmounted
    return () => {
      isMounted = false
    }
  }, [])

  // Marketplace: seed local selection state from redux product picks that already exist
  // (e.g. persisted from earlier in the session) so they show up pre-checked in the same
  // dropdown, instead of a separate marketplace-only Select tracking its own selection.
  useEffect(() => {
    if (!isMarketplace) return
    const marketplaceProducts = Array.isArray(products) ? products : []
    if (marketplaceProducts.length === 0) return

    setSelectedDataProducts((prev) => [
      ...prev,
      ...marketplaceProducts
        .filter((p: any) => !prev.some((existing) => existing.id === p.value))
        .map((p: any) => ({ id: p.value, name: p.label }))
    ])
    setAgentDataProducts((prev) => [
      ...prev,
      ...marketplaceProducts
        .filter((p: any) => !prev.some((existing) => existing.value === p.value))
        .map((p: any) => ({ label: p.label, value: p.value, type: 'dataProduct' }))
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMarketplace])

  // Fetch databox and data product details when IDs change
  useEffect(() => {
    // Reset if no available data sources
    if (!hasAvailableDataSources) {
      setDataboxes([])
      setAgentDataProducts([])
      fetchedIdsRef.current.clear()
      return
    }

    // Find new IDs that haven't been fetched yet
    const newDataboxIds = availableDataboxIds.filter((id) => !fetchedIdsRef.current.has(id))
    const newDataProductIds = availableDataProductIds.filter((id) => !fetchedIdsRef.current.has(id))

    // Skip if no new IDs to fetch
    if (newDataboxIds.length === 0 && newDataProductIds.length === 0) {
      return
    }

    // Mark as fetched immediately to prevent duplicate requests
    newDataboxIds.forEach((id) => fetchedIdsRef.current.add(id))
    newDataProductIds.forEach((id) => fetchedIdsRef.current.add(id))

    // This flag prevents setState calls after component unmounts during async operations
    let isMounted = true

    const fetchSources = async () => {
      // Only set loading state if component is still mounted
      if (!isMounted) return
      setIsFetchingSources(true)

      try {
        // Fetch only NEW databoxes in parallel
        const databoxPromises = newDataboxIds.map((id) =>
          getDataboxInfo(id, true).catch((err) => {
            console.error(`Error fetching databox ${id}:`, err)
            return null // Return null for failed fetches
          })
        )

        // Fetch only NEW data products in parallel
        const dataProductPromises = newDataProductIds.map((id) =>
          getDataProduct(id).catch((err: any) => {
            console.error(`Error fetching data product ${id}:`, err)
            return null // Return null for failed fetches
          })
        )

        // Await all promises in parallel for better performance
        const results = await Promise.all([...databoxPromises, ...dataProductPromises])

        // Early exit if component unmounted during fetch
        if (!isMounted) return

        // Split results back into databox and data product arrays
        const databoxResults = results.slice(0, databoxPromises.length)
        const dataProductResults = results.slice(databoxPromises.length)

        // Transform databox results to dropdown format, filtering out failures
        const fetchedDataboxes = databoxResults
          .filter((result: any) => result && (result.smartDocsDataboxId || result.id))
          .map((result: any) => {
            const teamIdentifier =
              result.teamId ||
              result.teamIdentifier ||
              result.scopeId ||
              result.smartDocsTeamId ||
              undefined

            const value = result.smartDocsDataboxId || result.id

            return {
              // Fall back to the raw id so a missing/failed name lookup still shows something
              label: result.name || value,
              value,
              type: 'databox',
              ...(teamIdentifier && { teamIdentifier }) // Only include if truthy
            }
          })
          .filter((item: any) => item.value !== null && item.value !== undefined)

        // Transform data product results to dropdown format, filtering out failures
        const fetchedDataProducts = dataProductResults
          .filter((result: any) => result && result.id)
          .map((result: any) => ({
            // Fall back to the raw id so a missing/failed name lookup still shows something
            label: result.name || result.id,
            value: result.id,
            type: 'dataProduct'
          }))
          .filter((item: any) => item.value !== null && item.value !== undefined)

        // Final check before updating state
        if (!isMounted) return

        // Merge new databoxes with existing ones (replace placeholders with real data)
        setDataboxes((prev) => {
          const merged = [...prev]
          fetchedDataboxes.forEach((newItem) => {
            const existingIndex = merged.findIndex((item) => item.value === newItem.value)
            if (existingIndex >= 0) {
              const existing = merged[existingIndex]
              // Replace placeholder with real data unless it already resolved to a real name
              // (still a placeholder if its label is empty or just echoes its own id/value)
              const isUnresolved = !existing.label || existing.label === existing.value
              if (isUnresolved) {
                // Preserve existing teamIdentifier (numeric ID) if it exists
                // Only use fetched teamIdentifier (GUID) as fallback
                merged[existingIndex] = {
                  ...newItem,
                  teamIdentifier: existing.teamIdentifier || newItem.teamIdentifier
                }
              }
            } else {
              // Add new item if not found
              merged.push(newItem)
            }
          })
          // Filter out any entries with undefined or null values
          return merged.filter((item) => item.value !== null && item.value !== undefined)
        })

        // Merge new data products with existing ones (replace placeholders with real data)
        setAgentDataProducts((prev) => {
          const merged = [...prev]
          fetchedDataProducts.forEach((newItem) => {
            const existingIndex = merged.findIndex((item) => item.value === newItem.value)
            if (existingIndex >= 0) {
              // Replace placeholder with real data unless it already resolved to a real name
              // (still a placeholder if its label is empty or just echoes its own id/value)
              const existing = merged[existingIndex]
              const isUnresolved = !existing.label || existing.label === existing.value
              if (isUnresolved) {
                merged[existingIndex] = newItem
              }
            } else {
              // Add new item if not found
              merged.push(newItem)
            }
          })
          // Filter out any entries with undefined or null values
          return merged.filter((item) => item.value !== null && item.value !== undefined)
        })
      } catch (error) {
        console.error('Error fetching sources:', error)
      } finally {
        // Only update loading state if component is still mounted
        if (isMounted) {
          setIsFetchingSources(false)
        }
      }
    }

    fetchSources()

    // Cleanup: Mark component as unmounted to prevent memory leaks
    return () => {
      isMounted = false
    }
  }, [availableDataboxIds.join(','), availableDataProductIds.join(','), hasAvailableDataSources])

  // Enrich selectedDataboxes with teamIdentifier from fetched databoxes
  useEffect(() => {
    if (databoxes.length === 0) return

    setSelectedDataboxes((prev) =>
      prev.map((selectedDb) => {
        // Find the fetched databox that matches this selected databox
        const fetchedDatabox = databoxes.find(
          (db) => db.value === (selectedDb.smartDocsDataboxId || selectedDb.id)
        )
        // Update with name from fetched databox, but preserve existing teamIdentifier
        if (fetchedDatabox) {
          return {
            ...selectedDb,
            name: fetchedDatabox.label || selectedDb.name,
            // Preserve existing teamIdentifier (numeric ID) if it exists
            // Only use fetched teamIdentifier (GUID) as fallback
            teamIdentifier: selectedDb.teamIdentifier || fetchedDatabox.teamIdentifier
          }
        }
        return selectedDb
      })
    )
  }, [databoxes.map((db) => `${db.value}-${db.label}-${db.teamIdentifier || ''}`).join(',')])

  // Enrich selectedDataProducts with names from fetched data products
  // Required because initialization creates placeholders with empty names, but parent needs real names
  useEffect(() => {
    if (agentDataProducts.length === 0) return

    setSelectedDataProducts((prev) =>
      prev.map((selectedDp) => {
        // Find the fetched data product that matches this selected data product
        const fetchedDataProduct = agentDataProducts.find((dp) => dp.value === selectedDp.id)
        // Update with name from fetched data
        if (fetchedDataProduct) {
          return {
            ...selectedDp,
            name: fetchedDataProduct.label || selectedDp.name
          }
        }
        return selectedDp
      })
    )
  }, [agentDataProducts.map((dp) => `${dp.value}-${dp.label}`).join(',')])

  // Fetch file details for this thread's narrowed file-level scope, falling back to the
  // agent's original scope - grouped by databox so files can be shown/unchecked per databox.
  useEffect(() => {
    let isMounted = true
    const cognitiveSearchIds = threadSources.cognitiveSearchIds?.length
      ? threadSources.cognitiveSearchIds
      : agentSources.cognitiveSearchIds || []

    if (cognitiveSearchIds.length === 0) {
      setFilesByDatabox({})
      setIsFetchingFileDetails(false)
      return
    }

    setIsFetchingFileDetails(true)

    const fetchFiles = async () => {
      try {
        const documents = await getDocumentDetails(cognitiveSearchIds)
        if (!isMounted || !Array.isArray(documents)) return

        const grouped: Record<string, any[]> = {}
        documents.forEach((doc: any, index: number) => {
          // API responses have used both field names for this - check both
          const docDataboxId = doc?.smartDocsDataboxId || doc?.databoxId
          if (!docDataboxId) return
          const databoxId = String(docDataboxId)
          if (!grouped[databoxId]) grouped[databoxId] = []
          grouped[databoxId].push({
            type: 'file',
            id: doc.externalId || doc.id || doc.cognitiveSearchId || cognitiveSearchIds[index],
            smartDocsDocumentId: doc.id,
            // Positional fallback if the API response doesn't include cognitiveSearchId directly
            cognitiveSearchId: doc.cognitiveSearchId || cognitiveSearchIds[index],
            name: doc.name
          })
        })

        if (isMounted) setFilesByDatabox(grouped)
      } catch (error) {
        console.error('Error fetching databox file details:', error)
      } finally {
        if (isMounted) setIsFetchingFileDetails(false)
      }
    }

    fetchFiles()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    threadSources.cognitiveSearchIds?.join(','),
    agentSources.cognitiveSearchIds?.join(','),
    agentSources.agentId
  ])

  // Fetch names for data products nested inside a databox - prefers this thread's own
  // narrowed groups, falling back to the agent's original groups, grouped by databox.
  useEffect(() => {
    let isMounted = true
    const dataProductsByDatabox = threadSources.dataProductsByDatabox?.length
      ? threadSources.dataProductsByDatabox
      : agentSources.dataProductsByDatabox || []

    if (dataProductsByDatabox.length === 0) {
      setProductsByDatabox({})
      setIsFetchingProductDetails(false)
      return
    }

    setIsFetchingProductDetails(true)

    const fetchProducts = async () => {
      try {
        const uniqueIds = [...new Set(dataProductsByDatabox.map((dp) => dp.dataProductId))]
        const results = await Promise.all(
          uniqueIds.map((id) =>
            getDataProduct(id).catch((error) => {
              console.error(`Error fetching databox data product ${id}:`, error)
              return null
            })
          )
        )
        if (!isMounted) return

        const namesById = new Map<string, string>()
        results.forEach((result: any, index) => {
          if (result?.name) namesById.set(uniqueIds[index], result.name)
        })

        const grouped: Record<string, any[]> = {}
        dataProductsByDatabox.forEach(({ dataProductId, databoxId }) => {
          if (!grouped[databoxId]) grouped[databoxId] = []
          grouped[databoxId].push({
            type: 'dataproduct',
            id: dataProductId,
            name: namesById.get(dataProductId) || dataProductId
          })
        })

        if (isMounted) setProductsByDatabox(grouped)
      } catch (error) {
        console.error('Error fetching databox data product details:', error)
      } finally {
        if (isMounted) setIsFetchingProductDetails(false)
      }
    }

    fetchProducts()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    threadSources.dataProductsByDatabox
      ?.map((dp) => `${dp.databoxId}:${dp.dataProductId}`)
      .join(','),
    agentSources.dataProductsByDatabox
      ?.map((dp) => `${dp.databoxId}:${dp.dataProductId}`)
      .join(','),
    agentSources.agentId
  ])

  // Attach fetched files and data products to their databox's selectedItems.
  useEffect(() => {
    if (hasUserInteractedRef.current) return
    // Wait for an in-flight refetch so a previous scope's items aren't attached
    if (isFetchingFileDetails || isFetchingProductDetails) return
    if (Object.keys(filesByDatabox).length === 0 && Object.keys(productsByDatabox).length === 0) {
      return
    }

    setSelectedDataboxes((prev) =>
      prev.map((db) => {
        const databoxId = String(db.smartDocsDataboxId || db.id)
        const items = [
          ...(filesByDatabox[databoxId] || []),
          ...(productsByDatabox[databoxId] || [])
        ]
        return items.length > 0 ? { ...db, selectedItems: items } : db
      })
    )
  }, [
    filesByDatabox,
    productsByDatabox,
    selectionInitVersion,
    isFetchingFileDetails,
    isFetchingProductDetails
  ])

  const modalsJSX = isSourcesModalOpen && (
    <DataSourcesModal
      isOpen={isSourcesModalOpen}
      toggle={() => setIsSourcesModalOpen(false)}
      initialTab={activeSourceTab}
      existingDataboxes={existingDataboxesForModal}
      lockedDataboxIds={Array.from(lockedDataboxIds)}
      lockedDataProductIds={Array.from(lockedDataProductIds)}
      lockedItemIds={Array.from(lockedItemIds)}
      lockedPersonalFileIds={Array.from(agentConfigSnapshot.personalDataboxResourceIds)}
      smartInsightsTeamIdentifier={agentSources.smartInsightsTeamIdentifier}
      useAgentWizardTeamContext={false}
      isTeamAgent={agentSources.isTeamAgent}
      isEnterpriseAgent={agentSources.isEnterpriseAgent}
      agentScopeId={agentSources.agentScopeId}
      removeDataboxFromAgent={(databoxIds: string[]) => {
        hasUserInteractedRef.current = true
        setSelectedDataboxes((prev) => {
          const next = prev.filter(
            (db) => !databoxIds.includes(String(db.smartDocsDataboxId || db.id))
          )
          updatePendingDataSourcesCache({ selectedDataboxes: next })
          return next
        })
      }}
      addDataboxToAgent={(newDataboxes: any) => {
        hasUserInteractedRef.current = true

        // Use what the modal sends as-is (it handles removals via removeDataboxFromAgent).
        // Keep selectedItems for file-level scoping, and `scope` to distinguish Personal
        // Databox entries from Team Databox ones.
        const databoxesToSave = newDataboxes.map((db: any) => ({
          id: db.id,
          name: db.name,
          smartDocsDataboxId: db.smartDocsDataboxId,
          teamIdentifier: db.teamIdentifier,
          ...(db.scope && { scope: db.scope }),
          ...(db.selectedItems?.length > 0 && { selectedItems: db.selectedItems })
        }))

        setSelectedDataboxes(databoxesToSave)
        updatePendingDataSourcesCache({ selectedDataboxes: databoxesToSave })
        setIsSourcesModalOpen(false)
      }}
      existingDataProducts={existingDataProductsForModal}
      removeDataProductFromAgent={(productIds: string[]) => {
        hasUserInteractedRef.current = true
        const filtered = selectedDataProducts.filter((dp) => !productIds.includes(String(dp.id)))
        setSelectedDataProducts(filtered)
        updatePendingDataSourcesCache({ selectedDataProducts: filtered })

        // Marketplace keeps its data product selection in redux (read by ChatInput and other
        // marketplace-page consumers) so it stays consistent with this selection.
        if (isMarketplace) {
          dispatch(setProducts(filtered.map((dp) => ({ label: dp.name, value: dp.id }))))
        }
      }}
      addDataProductToAgent={(dataProducts: any) => {
        hasUserInteractedRef.current = true

        // Smart merge: keep all existing and add only new ones
        const mergedProducts = [
          ...selectedDataProducts,
          ...dataProducts.filter(
            (dp: any) => !selectedDataProducts.some((existing) => existing.id === dp.id)
          )
        ]
        const dataProductsToSave = mergedProducts.map((dp: any) => ({ id: dp.id, name: dp.name }))
        setSelectedDataProducts(dataProductsToSave)
        updatePendingDataSourcesCache({ selectedDataProducts: dataProductsToSave })

        if (isMarketplace) {
          dispatch(setProducts(dataProductsToSave.map((dp) => ({ label: dp.name, value: dp.id }))))
        }

        setIsSourcesModalOpen(false)
      }}
    />
  )

  // Update the resolving data sources state whenever any of the fetching flags change
  useEffect(() => {
    setIsResolvingDataSources(
      isFetchingFileDetails || isFetchingProductDetails || isFetchingPersonalDatabox
    )
  }, [isFetchingFileDetails, isFetchingProductDetails, isFetchingPersonalDatabox])

  let mainContent = null
  if (isResolvingDataSources && (selectedDataboxes.length > 0 || isFetchingPersonalDatabox)) {
    mainContent = (
      <Badge
        label={
          <span className="flex items-center gap-[8px]">
            Data sources
            <Spinner size="sm" className="ml-1" />
          </span>
        }
        color="primary"
        type="pill"
        size="medium"
        className="cursor-pointer px-1 font-semibold theme-filter-badge-inactive-primary ml-[8px]"
      />
    )
  } else if (totalDataSourceCount > 0) {
    mainContent = (
      <div
        className={`ml-[8px] ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onMouseEnter={() => !isDisabled && setIsDataSourcesBadgeHovered(true)}
        onMouseLeave={() => setIsDataSourcesBadgeHovered(false)}
        data-testid="data-sources-badge-wrapper">
        <Badge
          label={
            <>
              <span id="data-sources-remove-all-icon" className="flex items-center gap-[8px]">
                Data sources{' '}
                {isDataSourcesBadgeHovered && !isDisabled && !isMatchingAgentConfig ? (
                  <span className="inline-flex">
                    <IconX
                      size={20}
                      stroke="currentColor"
                      data-testid="remove-all-data-sources-icon"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        setIsRemoveAllSourcesModalOpen(true)
                      }}
                    />
                  </span>
                ) : (
                  `(${totalDataSourceCount})`
                )}
              </span>
              {isMatchingAgentConfig && (
                <UncontrolledTooltip placement="top" target="data-sources-remove-all-icon">
                  Data sources match the agent&apos;s default configuration. Modify a file, databox,
                  or data product to enable reset.
                </UncontrolledTooltip>
              )}
            </>
          }
          color="primary"
          type="pill"
          size="medium"
          className={`px-1 font-semibold theme-filter-badge-inactive-primary ${
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={() => {
            if (isDisabled) return
            setActiveSourceTab('personalDatabox')
            setIsSourcesModalOpen(true)
          }}
        />
      </div>
    )
  }

  return (
    <>
      {mainContent}
      {modalsJSX}
      <RemoveAllDataSourcesModal
        isOpen={isRemoveAllSourcesModalOpen}
        toggle={() => setIsRemoveAllSourcesModalOpen(false)}
        onRemove={removeAllDataSources}
      />
    </>
  )
}
